/**
 * Whether this document is framed by a site that cannot share its cookies.
 *
 * What actually blocks a cookie is a cross-*site* parent, because `SameSite`
 * is defined on the registrable domain. This measures cross-*origin* instead,
 * which is a deliberate over-approximation.
 *
 * The reason is that cross-origin is the only thing measurable from inside the
 * frame. Reading `window.top.location.origin` throws for any cross-origin
 * parent, and that throw is the signal. Same-origin policy is origin-based, so
 * a same-site cross-origin parent (`app.newsela.com` framed in
 * `newsela.com`) throws too and also reads as embedded. Recovering the
 * parent's registrable domain would need `location.ancestorOrigins`, which
 * Firefox does not implement, plus a public-suffix list.
 *
 * The over-approximation is safe in both directions: the cookie shim runs
 * unnecessarily in a same-site frame, where the only effect is a more
 * permissive `SameSite` on cookies that were already readable, and
 * `useStorageAccess` requests access it would have been granted anyway.
 *
 * Note the origin comparison below only decides the same-origin case. Once the
 * read succeeds the parent is same-origin by definition, so a genuinely
 * cross-origin parent always arrives via the `catch`.
 *
 * The window is a parameter because `window.top` is not configurable, which
 * makes the global unmockable in tests.
 */
const isCrossOriginEmbedded = (win: Window | undefined = globalThis.window) => {
  if (!win) {
    return false;
  }

  try {
    if (win.self === win.top) {
      return false;
    }
    return win.top?.location.origin !== win.location.origin;
  } catch {
    return true;
  }
};

export default isCrossOriginEmbedded;
